import PageLayout from "@/components/PageLayout";
import { Smartphone, Zap, Signal, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const NETWORKS = [
  {
    name: "MTN Nigeria",
    prefix: "0803, 0806, 0703, 0706, 0813, 0816, 0810, 0814, 0903, 0906, 0913",
    delivery: "99%",
    speed: "< 5 sec",
    dnd: "Opt-out supported",
    color: "bg-amber-400",
    textColor: "text-amber-700",
    bgColor: "bg-amber-50 border-amber-200",
    status: "Excellent",
  },
  {
    name: "Airtel Nigeria",
    prefix: "0802, 0808, 0708, 0812, 0701, 0902, 0907, 0912",
    delivery: "98.5%",
    speed: "< 6 sec",
    dnd: "Opt-out supported",
    color: "bg-red-500",
    textColor: "text-red-700",
    bgColor: "bg-red-50 border-red-200",
    status: "Excellent",
  },
  {
    name: "Glo Nigeria",
    prefix: "0805, 0807, 0705, 0811, 0815, 0905, 0915",
    delivery: "97%",
    speed: "< 8 sec",
    dnd: "Opt-out supported",
    color: "bg-emerald-500",
    textColor: "text-emerald-700",
    bgColor: "bg-emerald-50 border-emerald-200",
    status: "Good",
  },
  {
    name: "9mobile (Etisalat)",
    prefix: "0809, 0817, 0818, 0908, 0909",
    delivery: "96%",
    speed: "< 10 sec",
    dnd: "Opt-out supported",
    color: "bg-lime-500",
    textColor: "text-lime-700",
    bgColor: "bg-lime-50 border-lime-200",
    status: "Good",
  },
];

const REGIONS = [
  { region: "Lagos", networks: "All 4", deliveryRate: "99.2%", note: "Best performance — dense network coverage" },
  { region: "Abuja (FCT)", networks: "All 4", deliveryRate: "99.0%", note: "Excellent across all networks" },
  { region: "Kano", networks: "MTN, Airtel, Glo", deliveryRate: "97.5%", note: "Strong northern coverage" },
  { region: "Rivers (Port Harcourt)", networks: "All 4", deliveryRate: "98.5%", note: "Good urban and peri-urban coverage" },
  { region: "Oyo (Ibadan)", networks: "All 4", deliveryRate: "98.3%", note: "Consistent delivery" },
  { region: "Katsina", networks: "MTN, Airtel", deliveryRate: "93.0%", note: "Rural coverage may vary" },
  { region: "Borno (Maiduguri)", networks: "MTN, Airtel", deliveryRate: "88.0%", note: "Active conflict areas may affect delivery" },
  { region: "Anambra", networks: "All 4", deliveryRate: "97.8%", note: "Good southeast coverage" },
  { region: "Delta", networks: "All 4", deliveryRate: "96.5%", note: "Consistent across urban centres" },
  { region: "Kaduna", networks: "MTN, Airtel, Glo", deliveryRate: "95.0%", note: "Varies in rural areas" },
  { region: "Cross River", networks: "All 4", deliveryRate: "94.5%", note: "Urban centres perform well" },
  { region: "Sokoto", networks: "MTN, Airtel", deliveryRate: "90.0%", note: "Remote areas may have lower delivery" },
];

const TIPS = [
  { icon: Smartphone, title: "Know Your Audience's Network", tip: "Check the number prefixes to identify which network your recipients are on. Tailor timing for network-specific peak hours." },
  { icon: Clock, title: "Best Sending Times", tip: "Weekdays 9 AM–12 PM and 3 PM–5 PM WAT consistently achieve the highest open and engagement rates across all Nigerian networks." },
  { icon: Zap, title: "Message Length", tip: "Keep messages under 160 characters for single-segment delivery. Longer messages split into multiple segments and may arrive out of order on some networks." },
  { icon: AlertTriangle, title: "DND Awareness", tip: "NCC's Do-Not-Disturb (DND) service is active. Transactional messages (OTP, delivery alerts) typically pass DND filters; promotional messages may not." },
];

export default function Coverage() {
  const seoProps = {
    title: "SMS Coverage — MTN, Airtel, Glo & 9mobile Network Delivery Rates",
    description: "Sendora provides 99%+ SMS delivery across all Nigerian networks — MTN, Airtel, Glo and 9mobile. See real-time coverage maps, delivery rates and DND status.",
    canonical: "/coverage",
    keywords: "SMS coverage Nigeria, MTN SMS delivery, Airtel SMS Nigeria, Glo SMS, 9mobile SMS, bulk SMS delivery rates Nigeria, DND Nigeria SMS",
    schema: [{
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": "https://www.sendoras.com/coverage",
      "name": "SMS Network Coverage — Sendora",
      "description": "Delivery rates and coverage across all Nigerian mobile networks.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sendoras.com" },
          { "@type": "ListItem", "position": 2, "name": "Coverage", "item": "https://www.sendoras.com/coverage" }
        ]
      }
    }]
  };
  return (
    <PageLayout title="Network Coverage" subtitle="Sendora delivers SMS across all four major Nigerian networks — 36 states and FCT." seo={seoProps}>

      {/* Overview stats */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { val: "4", label: "Networks Covered" },
              { val: "37", label: "States + FCT" },
              { val: "98%+", label: "Avg. Delivery Rate" },
              { val: "< 8 sec", label: "Avg. Delivery Time" },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-5 text-center shadow-sm">
                <p className="text-2xl font-extrabold text-[#0c1e41]">{s.val}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Network cards */}
          <h2 className="text-xl font-extrabold text-slate-900 mb-5">Network Performance</h2>
          <div className="grid sm:grid-cols-2 gap-5 mb-12">
            {NETWORKS.map(n => (
              <div key={n.name} className={`bg-white border rounded-xl p-5 shadow-sm ${n.bgColor}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-3 h-3 rounded-full shrink-0 ${n.color}`} />
                  <h3 className="font-extrabold text-slate-900">{n.name}</h3>
                  <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-white border ${n.textColor} border-current`}>{n.status}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Delivery Rate</span>
                    <span className="font-semibold text-slate-800">{n.delivery}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Avg. Speed</span>
                    <span className="font-semibold text-slate-800">{n.speed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">DND</span>
                    <span className="font-semibold text-slate-800">{n.dnd}</span>
                  </div>
                  <div className="pt-2 border-t border-current/10">
                    <span className="text-slate-500 text-xs">Number Prefixes: </span>
                    <span className="text-xs font-mono text-slate-700">{n.prefix}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Regional breakdown */}
          <h2 className="text-xl font-extrabold text-slate-900 mb-5">Regional Delivery Rates</h2>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm mb-12">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left px-5 py-3 font-semibold">State / Region</th>
                    <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Networks</th>
                    <th className="text-left px-5 py-3 font-semibold">Delivery Rate</th>
                    <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {REGIONS.map(r => (
                    <tr key={r.region} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 font-semibold text-sm text-slate-900">{r.region}</td>
                      <td className="px-5 py-3 hidden sm:table-cell text-xs text-slate-600">{r.networks}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          parseFloat(r.deliveryRate) >= 99 ? "bg-emerald-100 text-emerald-700" :
                          parseFloat(r.deliveryRate) >= 95 ? "bg-[#eef1f8] text-[#0c1e41]" :
                          parseFloat(r.deliveryRate) >= 90 ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }`}>{r.deliveryRate}</span>
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell text-xs text-slate-500">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Delivery tips */}
          <h2 className="text-xl font-extrabold text-slate-900 mb-5">Tips for Maximum Delivery</h2>
          <div className="grid sm:grid-cols-2 gap-5 mb-12">
            {TIPS.map(t => (
              <div key={t.title} className="flex gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="w-9 h-9 rounded-xl bg-[#eef1f8] border border-[#c0cfe8] flex items-center justify-center shrink-0">
                  <t.icon size={16} className="text-[#0c1e41]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-1">{t.title}</p>
                  <p className="text-xs text-slate-600 leading-relaxed">{t.tip}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#0c1e41] to-[#061224] rounded-2xl p-8 text-center text-white">
            <h2 className="text-xl font-extrabold mb-2">Start Sending to All Nigerian Networks</h2>
            <p className="text-[#adc0e0] text-sm mb-5">Get your account set up in 60 seconds. No contract. No minimum spend.</p>
            <Link to="/register" className="inline-block bg-white text-[#0c1e41] font-semibold px-6 py-2.5 rounded-xl hover:bg-[#eef1f8] transition-colors shadow-md text-sm">
              Create Free Account
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
